import type { AdminRhythmBaselineWeek, RhythmBaselineArm, RhythmShare } from '@steado/admin-contract/rhythm';
import type { ReactNode } from 'react';

import { Panel } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import { formatLocalDate } from '@/lib/format';

import { count, perThousand, shareCounts, sharePercent } from './rhythm-format';

interface Row {
  readonly label: string;
  /** What is counted, in the words the number would be challenged with. */
  readonly meaning: string;
  readonly cell: (week: AdminRhythmBaselineWeek) => { readonly value: string; readonly detail?: string };
}

const share = (pick: (week: AdminRhythmBaselineWeek) => RhythmShare): Row['cell'] => {
  return (week) => {
    const value = pick(week);
    return { value: sharePercent(value), detail: shareCounts(value) };
  };
};

const optOut = (category: 'reminders' | 'summaries' | 'tips'): Row['cell'] => {
  return (week) => {
    const sends = week.guardrails.sends[category];
    const optOuts = week.guardrails.optOuts[category];
    return {
      value: perThousand(optOuts, sends),
      detail: `${count(optOuts)} off, ${count(sends)} sent`,
    };
  };
};

const RETENTION: readonly Row[] = [
  {
    label: 'Logged that week',
    meaning: 'Users with a logged meal, a completed workout or a weigh-in.',
    cell: (week) => ({ value: count(week.activeUsers) }),
  },
  {
    label: 'Kept week',
    meaning: 'Of those, the ones whose logged days reached their weekly target.',
    cell: share((week) => week.keptWeek),
  },
  {
    label: 'Opened the app',
    meaning:
      'Users with a recorded app session. Only app builds with the session recorder send them, so it rises as people update; read it against itself.',
    cell: (week) =>
      week.openedApp === null
        ? { value: '—', detail: 'before sessions were recorded' }
        : { value: count(week.openedApp) },
  },
  {
    label: 'Current-user retention',
    meaning: 'Of users who logged in each of the two weeks before, those who logged in this one.',
    cell: share((week) => week.currentUserRetention),
  },
  {
    label: 'Week-one activation',
    meaning: 'Of sign-ups whose first seven days ended this week, those who logged on three or more.',
    cell: share((week) => week.weekOneActivation),
  },
  {
    label: 'Day 1',
    meaning: 'Of sign-ups whose day 1 fell in this week, those who logged on it.',
    cell: share((week) => week.d1),
  },
  { label: 'Day 7', meaning: 'The same for day 7.', cell: share((week) => week.d7) },
  { label: 'Day 30', meaning: 'The same for day 30.', cell: share((week) => week.d30) },
  {
    label: 'Back within 7 days',
    meaning: 'Of users who began slipping the week before, those who came back within seven days.',
    cell: share((week) => week.reactivation),
  },
  {
    label: '7-day streak',
    meaning:
      'Of the users who kept a day, those whose streak stood at seven days or more as the week ended. The measure’s headline figure.',
    cell: (week) =>
      week.sevenDayStreak === null
        ? { value: '—', detail: 'before the ledger' }
        : { value: sharePercent(week.sevenDayStreak), detail: shareCounts(week.sevenDayStreak) },
  },
];

/** Phase 2's exit: the arms are compared on this, with reminder opt-outs below. */
const EVENING_NUDGE: readonly Row[] = [
  {
    label: 'Acted after it',
    meaning:
      'Of evening streak nudges sent, those followed by a meal, workout or weigh-in the same day. Compare the arms: the bar is five points above the holdout.',
    cell: share((week) => week.actedAfterEveningNudge),
  },
];

const ARMS: readonly { readonly value: RhythmBaselineArm; readonly label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'rhythm', label: 'Rhythm arm' },
  { value: 'holdout', label: 'Holdout' },
];

const GUARDRAILS: readonly Row[] = [
  {
    label: 'Reminders switched off',
    meaning: 'Users who turned reminder pushes off, per 1,000 reminder pushes sent.',
    cell: optOut('reminders'),
  },
  { label: 'Summaries switched off', meaning: 'The same for summaries.', cell: optOut('summaries') },
  { label: 'Tips switched off', meaning: 'The same for tips.', cell: optOut('tips') },
  {
    label: 'Permission turned off',
    meaning: 'Devices whose notification permission was switched off in the phone’s settings.',
    cell: (week) => ({ value: count(week.guardrails.osDenied) }),
  },
  {
    label: 'App removed',
    meaning: 'Devices whose push token the provider rejected.',
    cell: (week) => ({ value: count(week.guardrails.providerRejected) }),
  },
];

function Section({
  title,
  rows,
  weeks,
}: {
  readonly title: string;
  readonly rows: readonly Row[];
  readonly weeks: readonly AdminRhythmBaselineWeek[];
}): ReactNode {
  return (
    <>
      <tr className="border-t border-border bg-secondary/40">
        <th
          scope="colgroup"
          colSpan={weeks.length + 1}
          className="px-[18px] py-1.5 text-left text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase"
        >
          {title}
        </th>
      </tr>
      {rows.map((row) => (
        <tr key={row.label} className="border-t border-border align-top">
          <th scope="row" className="px-[18px] py-2.5 text-left font-normal">
            <span className="block text-[13px] font-medium text-foreground">{row.label}</span>
            <span className="block max-w-[340px] text-xs text-pretty text-muted-foreground">
              {row.meaning}
            </span>
          </th>
          {weeks.map((week) => {
            const cell = row.cell(week);
            return (
              <td key={week.weekStart} className="px-3 py-2.5 text-right whitespace-nowrap">
                <span className="block font-mono text-[13px] font-medium text-foreground">{cell.value}</span>
                {cell.detail === undefined ? null : (
                  <span className="block text-xs text-muted-foreground">{cell.detail}</span>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/**
 * The numbers Rhythm is judged against, one column per complete week.
 *
 * Metrics down the side and weeks across, because the question is always
 * "did this number move", and that is read along a row. Every share shows its
 * two counts under the percentage: at this size a percentage alone hides
 * whether it stands on four users or four hundred. A dash means nobody was
 * in the denominator, which is not the same as nobody succeeding.
 */
export function BaselinePanel({
  weeks,
  arm,
  onArmChange,
}: {
  readonly weeks: readonly AdminRhythmBaselineWeek[];
  readonly arm: RhythmBaselineArm;
  readonly onArmChange: (arm: RhythmBaselineArm) => void;
}) {
  return (
    <Panel
      title="Baseline"
      description="The last four complete weeks, Monday to Sunday, counted from the logs. A row does not change once its week has ended. One arm counts the users in it today."
      action={
        <div className="flex gap-1" role="group" aria-label="Whose baseline">
          {ARMS.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="xs"
              variant={arm === option.value ? 'secondary' : 'ghost'}
              aria-pressed={arm === option.value}
              onClick={() => {
                onArmChange(option.value);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      }
      bleed
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          “Logged” means a meal, a completed workout or a weigh-in: app opens have no history before sessions
          are recorded, and the baseline has to mean the same thing in every week. Complaints from support and
          store reviews are counted by hand and are not on this screen.
        </p>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr className="border-t border-border">
              <th scope="col" className="px-[18px] py-2 text-left text-xs font-medium text-muted-foreground">
                Week of
              </th>
              {weeks.map((week) => (
                <th
                  key={week.weekStart}
                  scope="col"
                  className="px-3 py-2 text-right text-xs font-medium whitespace-nowrap text-muted-foreground"
                >
                  {formatLocalDate(week.weekStart)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Section title="Retention" rows={RETENTION} weeks={weeks} />
            <Section title="Evening nudge" rows={EVENING_NUDGE} weeks={weeks} />
            <Section title="Guardrails" rows={GUARDRAILS} weeks={weeks} />
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
